/**
 * Resolves the API base URL, in one place, defensively.
 *
 * Every caller builds request URLs by appending directly — `${API_BASE}/devices`,
 * `${API_BASE}/auth/breakglass` — so the base **must** already end in `/api`.
 *
 * Setting REACT_APP_API_URL to the bare origin (`https://console.assetmesh.io`)
 * instead of `https://console.assetmesh.io/api` produced requests to
 * `/auth/breakglass`. That path does not match the `/api/*` rewrite, so it fell
 * through to the SPA's index.html, which cannot accept a POST and returned
 * **405 Method Not Allowed**. The request never reached the backend at all, and
 * every API call in the app was silently going to the wrong URL — the break-glass
 * login just happened to be where it was noticed.
 *
 * The suffix is therefore normalized rather than assumed. Documenting "must
 * include /api" was not enough: the failure mode is invisible, produces a
 * plausible-looking error, and leaves nothing in the server logs to find.
 */

const DEV_DEFAULT = 'http://127.0.0.1:5001/api';

// Same-origin in production — the SPA and API are served by one Vercel project.
const PROD_DEFAULT = '/api';

export function resolveApiBase(rawValue, nodeEnv) {
  const configured = (rawValue || '').trim();

  if (!configured) {
    return nodeEnv === 'production' ? PROD_DEFAULT : DEV_DEFAULT;
  }

  // Drop trailing slashes so '/api/' and '/api' behave identically.
  const trimmed = configured.replace(/\/+$/, '');

  if (/\/api$/i.test(trimmed)) {
    return trimmed;
  }

  // Configured without the suffix — append it rather than issuing requests that
  // bypass the /api/* rewrite.
  const corrected = `${trimmed}/api`;
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(
      `[apiBase] REACT_APP_API_URL is "${configured}", which does not end in ` +
      `"/api". Using "${corrected}" instead — without the suffix, requests do ` +
      `not match the /api/* route and return 405 from the static site.`
    );
  }
  return corrected;
}

export const API_BASE = resolveApiBase(
  process.env.REACT_APP_API_URL,
  process.env.NODE_ENV
);

export default API_BASE;
